import React, { useEffect, useState } from 'react';
import logo from '../../assets/Logo_Only_Icon.png';
import translation from '../../assets/tool-brand/translation-management/icon.svg';
import solution from '../../assets/tool-brand/solution-builder/icon.svg';
import security from '../../assets/tool-brand/security-role-manager/icon.svg';
import plugin from '../../assets/tool-brand/plugin-registration/icon.svg';
import priceList from '../../assets/tool-brand/price-list-manager/icon.svg';
import recordImport from '../../assets/tool-brand/csv-record-import/icon.svg';
import bulkData from '../../assets/tool-brand/bulk-data-operations/icon.svg';
import pcf from '../../assets/tool-brand/pcf-builder/icon.svg';
import fetchXml from '../../assets/tool-brand/fetchxml-builder/icon.svg';
import drift from '../../assets/tool-brand/environment-drift-monitor/icon.svg';
import formDesigner from '../../assets/tool-brand/form-designer/icon.svg';
import { WORKSPACE_TOOL_COUNT } from './landingFacts';
import '../../styles/PortalFilm.scss';

const chapters = ['Gli strumenti', 'Le persone', 'Un unico portale'];
const toolIcons = [translation, plugin, solution, recordImport, bulkData, security, pcf, fetchXml, drift, formDesigner];
const environments = ['DEV', 'TEST', 'PROD'];
const roles = [
    { name: 'Utenti', task: 'Il lavoro di ogni giorno', icon: translation },
    { name: 'Configuratori', task: 'Dare forma ai processi', icon: solution },
    { name: 'Venditori', task: 'Listini sempre aggiornati', icon: priceList },
    { name: 'Admin', task: 'Tenere tutto sotto controllo', icon: security },
    { name: 'Sviluppatori', task: 'Costruire nuove possibilità', icon: plugin },
];

/** A local animated introduction to the workspace, not a recording of the product. */
export default function PortalFilm() {
    const [chapter, setChapter] = useState(0);
    const [paused, setPaused] = useState(false);
    const [reducedMotion, setReducedMotion] = useState(() => window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    const [visible, setVisible] = useState(true);

    useEffect(() => {
        const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
        const update = () => setReducedMotion(preference.matches);
        preference.addEventListener('change', update);
        const visibility = () => setVisible(!document.hidden);
        document.addEventListener('visibilitychange', visibility);
        return () => {
            preference.removeEventListener('change', update);
            document.removeEventListener('visibilitychange', visibility);
        };
    }, []);

    useEffect(() => {
        if (paused || reducedMotion || !visible) return;
        const timer = window.setTimeout(() => {
            setChapter(value => (value + 1) % chapters.length);
        }, 4000);
        return () => window.clearTimeout(timer);
    }, [paused, reducedMotion, visible, chapter]);

    const select = (index: number) => { setChapter(index); setPaused(true); };
    const stopped = paused || reducedMotion || !visible;

    return <section className={`sp-film${stopped ? ' is-paused' : ''}`} aria-label="Semplora in tre scene: strumenti, persone, portale">
        <div className="sp-film__heading"><span><i /> SEMPLORA / IN BREVE</span></div>
        <div className="sp-film__screen" aria-live="off">
            <div className="sp-film__orbits" aria-hidden="true"><i /><i /><i /></div>
            <div className="sp-film__scene" key={chapter}>
                {chapter === 0 && <>
                    <p className="sp-film__kicker">01 / TUTTO A PORTATA DI MANO</p>
                    <p className="sp-film__title">{WORKSPACE_TOOL_COUNT} tool, un solo accesso.<br /><span>I tuoi ambienti sotto mano.</span></p>
                    <div className="sp-film__toolgrid" aria-hidden="true">{toolIcons.map(icon => <img key={icon} src={icon} alt="" width="44" height="44" />)}</div>
                    <div className="sp-film__envs" aria-hidden="true">
                        {environments.map(name => <span key={name} className={name === 'TEST' ? 'is-active' : undefined}>{name}</span>)}
                    </div>
                </>}
                {chapter === 1 && <>
                    <p className="sp-film__kicker">02 / METTERE LE PERSONE AL CENTRO</p>
                    <p className="sp-film__title">Il tuo ruolo.<br /><span>Il tuo prossimo risultato.</span></p>
                    <div className="sp-film__roles">{roles.map(role => <div key={role.name}><img src={role.icon} alt="" width="32" height="32" /><span><strong>{role.name}</strong><small>{role.task}</small></span></div>)}</div>
                </>}
                {chapter === 2 && <>
                    <p className="sp-film__kicker">03 / TUTTO TROVA IL SUO POSTO</p>
                    <div className="sp-film__hub"><img src={logo} alt="Semplora" width="150" height="150" /><span>STRUMENTI · PERSONE · AMBIENTI</span></div>
                    <p className="sp-film__title">Un unico portale.<br /><span>Nel tuo browser.</span></p>
                </>}
            </div>
        </div>
        <div className="sp-film__controls">
            <div role="group" aria-label="Scene del banner">{chapters.map((name, index) => <button type="button" key={name} aria-label={`Scena ${index + 1}: ${name}`} aria-pressed={chapter === index} onClick={() => select(index)}><span>{String(index + 1).padStart(2, '0')}</span><i key={chapter === index ? `active-${chapter}` : 'idle'} /></button>)}</div>
            {!reducedMotion && <button className="sp-film__pause" type="button" onClick={() => setPaused(value => !value)} aria-label={paused ? 'Riprendi il banner animato' : 'Metti in pausa il banner animato'}>{paused ? '▶ Riprendi' : 'Ⅱ Pausa'}</button>}
            {reducedMotion && <span className="sp-film__static">Esplora le scene</span>}
        </div>
        <p className="sp-film__disclaimer">Per Dynamics 365, Dataverse e Power Platform, direttamente nel browser.</p>
    </section>;
}
